// Pure, framework-free pagination helpers. Kept side-effect free so they can be
// unit-tested without React or the DOM.

export const RESULTS_PAGE_SIZE = 25;

const range = (start, end) => {
  const out = [];
  for (let i = start; i <= end; i += 1) out.push(i);
  return out;
};

// Given a total item count and a requested page, return the clamped page plus
// the slice bounds and a 1-based inclusive display range (from–to of total).
export function paginate(total, requestedPage, pageSize = RESULTS_PAGE_SIZE) {
  const safeTotal = Math.max(0, Number(total) || 0);
  const size = Math.max(1, Number(pageSize) || RESULTS_PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(safeTotal / size));
  const page = Math.min(Math.max(1, Number(requestedPage) || 1), pageCount);
  const startIndex = (page - 1) * size;
  const endIndex = Math.min(startIndex + size, safeTotal);
  return {
    page,
    pageCount,
    startIndex,
    endIndex,
    from: safeTotal === 0 ? 0 : startIndex + 1,
    to: endIndex,
    total: safeTotal
  };
}

// Build the sequence of page buttons with ellipsis gaps, e.g.
// [1, 'left-ellipsis', 4, 5, 6, 'right-ellipsis', 12]. `siblings` controls how
// many pages flank the current one.
export function getPageNumbers(currentPage, pageCount, siblings = 1) {
  const total = Math.max(1, Number(pageCount) || 1);
  const current = Math.min(Math.max(1, Number(currentPage) || 1), total);
  const totalShown = siblings * 2 + 5; // first, last, current, 2 ellipses

  if (total <= totalShown) {
    return range(1, total);
  }

  const leftSibling = Math.max(current - siblings, 1);
  const rightSibling = Math.min(current + siblings, total);
  const showLeftDots = leftSibling > 2;
  const showRightDots = rightSibling < total - 1;
  const edgeCount = 3 + 2 * siblings;

  if (!showLeftDots && showRightDots) {
    return [...range(1, edgeCount), 'right-ellipsis', total];
  }
  if (showLeftDots && !showRightDots) {
    return [1, 'left-ellipsis', ...range(total - edgeCount + 1, total)];
  }
  return [1, 'left-ellipsis', ...range(leftSibling, rightSibling), 'right-ellipsis', total];
}
